import { useState } from 'react';

import { Box, Drawer, SxProps, Theme } from '@mui/material';

import { useLanguage } from '../../hooks';
import { ActivatedMobileUserMinimumData, UserGroup } from '../../types';
import { DynamicSignatureFormQuestion } from '../../types/taskForm';
import { DynamicSignerDrawerContentSelector } from './DynamicSignerDrawerContentSelector';
import { SignersDrawerFooter, SignersDrawerFooterProps } from './SignersDrawerFooter';
import { SignerTypeDrawerContentSelector } from './SignerTypeDrawerContentSelector';
import { SignerType } from './types';
import { UserDrawerContentSelector } from './UserDrawerContentSelector';

type SignatureDrawerSelector =
  | 'signerTypeSelector'
  | 'fixedSignerSelector'
  | 'dynamicSignerSelector';

const customStyles: Record<string, SxProps<Theme>> = {
  drawerColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  contentBox: {
    flex: 1,
    overflow: 'hidden',
  },
};

interface SignatureConfigurationDrawerSelectorProps {
  open: boolean;
  onClose: () => void;
  users: ActivatedMobileUserMinimumData[];
  groups: UserGroup[];
  onFixedSignersSelected: (
    users: ActivatedMobileUserMinimumData[],
    externalUsers: string[],
    isLocationActive: boolean,
  ) => void;
  initialSelectedUsers: ActivatedMobileUserMinimumData[];
  initialExternalUsers: string[];
  initialLocationChecked?: boolean;
  fixedSignerCount: number;
  dynamicSignerCount: number;
  dynamicSignatureQuestionsAvailable: DynamicSignatureFormQuestion[];
  dynamicSignatureQuestionsSelected: DynamicSignatureFormQuestion[];
  onDynamicSignersSelected: (selectedQuestions: DynamicSignatureFormQuestion[]) => void;
}

export function SignatureConfigurationDrawerSelector({
  open,
  onClose,
  users,
  groups,
  onFixedSignersSelected,
  initialSelectedUsers = [],
  initialExternalUsers = [],
  initialLocationChecked,
  fixedSignerCount,
  dynamicSignerCount,
  dynamicSignatureQuestionsAvailable,
  dynamicSignatureQuestionsSelected = [],
  onDynamicSignersSelected,
}: SignatureConfigurationDrawerSelectorProps) {
  const lang = useLanguage();
  const [activeDrawerSelector, setActiveDrawerSelector] =
    useState<SignatureDrawerSelector>('signerTypeSelector');
  const [fixedSigner, setFixedSigners] = useState<{
    selectedUsers: ActivatedMobileUserMinimumData[];
    externalUsers: string[];
    isLocationActive: boolean;
  } | null>(null);
  const [dynamicSigners, setDynamicSigners] = useState<string[]>([]);

  const handleClose = () => {
    setActiveDrawerSelector('signerTypeSelector');
    setFixedSigners(null);
    setDynamicSigners([]);
    onClose();
  };

  const handleSignerTypeClick = (signerType: SignerType) => {
    if (signerType === SignerType.FIXED) {
      setActiveDrawerSelector('fixedSignerSelector');
      return;
    }

    setActiveDrawerSelector('dynamicSignerSelector');
  };

  const handleFixedSave = (
    selectedUsers: ActivatedMobileUserMinimumData[],
    externalUsers: string[] = [],
    isLocationActive = false,
  ) => {
    onFixedSignersSelected(selectedUsers, externalUsers, isLocationActive);
  };

  const handleDynamicSignersSelected = (
    dynamicSignatureQuestions: DynamicSignatureFormQuestion[],
  ) => {
    onDynamicSignersSelected(dynamicSignatureQuestions);
  };

  const handleFixedSignersUpdate = (
    selectedUsers: ActivatedMobileUserMinimumData[],
    externalUsers: string[],
    isLocationActive: boolean,
  ) => {
    setFixedSigners({ selectedUsers, externalUsers, isLocationActive });
  };

  const getFooterActions = (): SignersDrawerFooterProps | null => {
    switch (activeDrawerSelector) {
      case 'signerTypeSelector':
        return {
          onAddButtonClick: handleClose,
          onCancelButtonClick: handleClose,
        };
      case 'fixedSignerSelector':
        if (!fixedSigner) {
          return null;
        }
        return {
          onAddButtonClick: () => {
            handleFixedSave(
              fixedSigner.selectedUsers,
              fixedSigner.externalUsers,
              fixedSigner.isLocationActive,
            );
            handleClose();
          },
          addButtonDisabled: false,
          onCancelButtonClick: handleClose,
        };
      case 'dynamicSignerSelector':
        return {
          onAddButtonClick: () => {
            const selectedQuestions = dynamicSignatureQuestionsAvailable.filter((q) =>
              dynamicSigners.includes(String(q.id)),
            );
            handleDynamicSignersSelected(selectedQuestions);
            handleClose();
          },
          addButtonDisabled: dynamicSigners.length === 0,
          onCancelButtonClick: handleClose,
        };
      default:
        return null;
    }
  };

  const content = () => {
    switch (activeDrawerSelector) {
      case 'signerTypeSelector':
        return (
          <SignerTypeDrawerContentSelector
            onClose={handleClose}
            onSignerTypeClick={handleSignerTypeClick}
            fixedSignerCount={fixedSignerCount}
            dynamicSignerCount={dynamicSignerCount}
          />
        );
      case 'fixedSignerSelector':
        return (
          <UserDrawerContentSelector
            onClose={handleClose}
            groups={groups}
            users={users}
            onSave={handleFixedSave}
            initialSelectedUsers={initialSelectedUsers}
            initialExternalUsers={initialExternalUsers}
            initialLocationChecked={initialLocationChecked}
            singleUser={false}
            isUserAssigner
            title={lang.formSignatureBuilder.fixedSignerTitle}
            onBack={() => setActiveDrawerSelector('signerTypeSelector')}
            onSignersUpdate={handleFixedSignersUpdate}
          />
        );
      case 'dynamicSignerSelector':
        return (
          <DynamicSignerDrawerContentSelector
            open={open}
            onClose={handleClose}
            onDynamicSignersSelected={handleDynamicSignersSelected}
            onBack={() => setActiveDrawerSelector('signerTypeSelector')}
            dynamicSignatureQuestionsAvailable={dynamicSignatureQuestionsAvailable}
            dynamicSignatureQuestionsSelected={dynamicSignatureQuestionsSelected}
            onSelectedQuestionIdsChange={setDynamicSigners}
          />
        );
      default:
        return null;
    }
  };

  const footerActions = getFooterActions();

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={handleClose}
    >
      <Box sx={customStyles.drawerColumn}>
        <Box sx={customStyles.contentBox}>{content()}</Box>
        {footerActions && (
          <SignersDrawerFooter
            onAddButtonClick={footerActions.onAddButtonClick}
            addButtonDisabled={footerActions.addButtonDisabled}
            onCancelButtonClick={footerActions.onCancelButtonClick}
          />
        )}
      </Box>
    </Drawer>
  );
}
